/* eslint-disable */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.cacheDOMElements();
    /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
    this.onSubmitBound = this.onSubmit.bind(this);
    /* ===== Remove the noscript elements from the DOM (javascript is enabled) ===== */
    Array.from(this.noscriptElements).forEach(element => element.remove());
    
    // Add Pincode Checker
    this.addPincodeChecker();
  }

  cacheDOMElements() {
    /* ===== Cache the DOM elements ===== */
    this.form = this.querySelector('form');
    this.noscriptElements = this.querySelectorAll('noscript');
    this.sectionId = this.getAttribute('data-section-id');
    this.productInformation = this.closest('product-information');
    this.cartAction = document.getElementById('PageContainer').dataset.cartAction;
    this.cartType = document.getElementById('PageContainer').dataset.cartType;
    this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
    this.giftWrapProductId = this.getAttribute('data-gift-wrap-product-id');
    this.wethemeGlobal = document.querySelector('script#wetheme-global');
    this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
    this.qtyErrorMessage = this.querySelector('[data-qty-error-message]');
  }

  connectedCallback() {
    /* ===== Set the initial state of the add to cart button ===== */
    if (!this.form) return;
    this.attachEventListeners();
  }

  attachEventListeners() {
    /* ===== Attach the event listeners to the DOM elements ===== */
    this.form.addEventListener('submit', this.onSubmitBound);
  }

  onSubmit(event) {
    /* ===== Handle the form submission ===== */
    event.preventDefault();
    this.prepareForSubmission();
  }

  prepareForSubmission() {
    /* ===== Prepare the form for submission ===== */
    this.cacheSubmissionElements();
    if (!this.checkRequiredInputs()) return;
    this.disableBlankInputs();
    // If the form is valid, proceed with the submission
    this.showLoadingState();
    this.addItemsToCart();
  }

  cacheSubmissionElements() {
    /* ===== Cache the elements needed for form submission ===== */
    this.loadingIcon = this.form.querySelector('[data-loading-icon]');
    this.addToCartText = this.form.querySelector('[data-add-to-cart-text]');
    this.addedText = this.form.querySelector('[data-cart-added-text]');
    this.quantityInput = this.form.querySelector('[name="quantity"]');
    this.quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
  }

  disableBlankInputs() {
    // Select all input and textarea elements
    const elements = this.productInformation.querySelectorAll('input, textarea');
    this.blankElements = [];

    elements.forEach((element) => {
      // Check if the element has no value
      if (!element.value) {
        // Temporarily disable the element so that it is not included in the form submission
        element.disabled = true;
        this.blankElements.push(element);
      }
    });
  }

  enableBlankInputs() {
    // Enable the blank elements that were disabled
    this.blankElements.forEach((element) => {
      element.disabled = false;
    });
    this.blankElements = [];
  }

  fetchConfig(type = 'json') {
    /* ===== Create the fetch configuration object ===== */
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
    };
  }

  checkRequiredInputs() {
    /* ===== Check if the required inputs have been filled out ===== */
    const requiredInputs = Array.from(this.productInformation.querySelectorAll('[required]:not(:disabled)'));
    this.clearErrorMessages();
  
    const invalidInputs = requiredInputs.filter(input => {
      if (input.type === 'checkbox') {
        return !input.checked;
      } else {
        return !input.value.trim();
      }
    });
  
    invalidInputs.forEach(input => this.applyErrorMessage(input));
    return invalidInputs.length === 0;
  }  

  clearErrorMessages() {
    /* ===== Clear the error messages from the form ===== */
    this.productInformation.querySelectorAll('.required-field-error').forEach(error => error.remove());
  }

  applyErrorMessage(input) {
    /* ===== Apply the error message to the input element if the required input is empty ===== */
    if (!input) return;
    const inputId = input.id;
    const buttonWrapper = this.querySelector('.paymentButtonsWrapper');
    const errorContainer = input.closest('.input-wrapper')?.querySelector('.input-error-container');
    const errorMessage = input.hasAttribute('custom-dropdown-input') ? this.translationsObject.translations.required_dropdown_error : input.type === 'file' ? this.translationsObject.translations.required_upload_error : input.type === 'checkbox' ? this.translationsObject.translations.required_checkbox_error : this.translationsObject.translations.required_input_error;
    const errorElement = `<span id="${inputId}" class="required-field-error errors" style="display: block;">${errorMessage}</span>`;
    const submitErrorMessage = input.type == 'file' ? input.closest('file-upload')?.dataset.errorMessage : input.dataset.errorMessage;
    const submitErrorElement = `<span id="${inputId}Submit" class="required-field-error errors" style="display: block;">${submitErrorMessage}</span>`;
    errorContainer ? errorContainer.innerHTML = errorElement : input.insertAdjacentHTML('beforebegin', errorElement);
    if (buttonWrapper) buttonWrapper.insertAdjacentHTML('beforebegin', submitErrorElement);
    input.focus();

    // If the input has an error message, add an 'input' event listener to remove the error message
    if (input.type == 'file') return;
    input.addEventListener('input', () => {
      this.productInformation.querySelector(`#${inputId}.required-field-error`)?.remove();
      this.querySelector(`#${inputId}Submit.required-field-error`)?.remove();
    }, { once: true });
  }

  giftWrapEnabled() {
    /* ===== Determine if the gift wrap product is enabled ===== */
    return !!this.productInformation.querySelector('[name="properties[Gift wrap]"]')?.checked && this.giftWrapProductId !== '';
  }

  async addItemsToCart() {
    /* ===== Add the item(s) to the cart ===== */
    try {
      // Add the main product to the cart
      const mainProductAdded = await this.addProductToCart(routes.cart_add_url, this.fetchConfigWithBody());
      if (mainProductAdded && this.giftWrapEnabled()) {
        // Add the gift wrap product to the cart
        await this.addProductToCart(routes.cart_add_url, this.additionalProductConfig(this.giftWrapProductId, this.quantity));
      }
      // Process the response
      this.processResponse(mainProductAdded);
    } catch (error) {
      console.error(`Error adding item(s) to cart: ${error.description || error.message}`);
    }
  }

  async addProductToCart(url, config) {
    /* ===== Add the product to the cart ===== */
    const response = await fetch(url, config);
    if (response.ok) {
      // Update cart drawer
      if (this.cartType === 'drawer') {
        const responseJson = await response.json();
        this.updateCartDrawer(responseJson);
      }
    } else {
      console.error(`Error adding item to cart: ${response.description || response.statusText}`);
    }
    return response;
  }

  additionalProductConfig(productId, quantity) {
    /* ===== Create the configuration object for the additional product(s) to be added to the cart ===== */
    const additionalItemPayload = { items: [{ id: productId, quantity: quantity }] };
    if (this.cartType === 'drawer') {
      additionalItemPayload['sections'] = 'cart-drawer';
    }
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(additionalItemPayload)
    };
  }

  updateCartDrawer(responseJson) {
    eventBus.emit('update:cart:drawer', responseJson);
  }

  async getCart() {
    /* ===== Fetch the cart information ===== */
    try {
      const response = await fetch(`${routes.cart_url}?view=compare`);
      return await response.json();
    } catch (error) {
      console.error(`Error fetching cart: ${error.description || error.message}`);
    }
  }

  updateCartCountIndicator() {
    /* ===== Update the cart count indicator ===== */
    if (!this.cartCountIndicator) return;
    this.getCart().then(cart => window.wetheme.updateCartCount(cart));
  }

  fetchConfigWithBody() {
    /* ===== Create the fetch configuration object with the form data as the body ===== */
    const config = this.fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];
    config.body = this.addSectionsParam(new FormData(this.form));
    return config;
  }

  addSectionsParam(formData) {
    /* ===== Add the sections parameter to the form data for cart drawer rendering ===== */
    if (this.cartType === 'drawer') {
      formData.append('sections', 'cart-drawer');
    }
    return formData;
  }

  processResponse(response) {
    /* ===== Process the response from the server ===== */
    if (response.ok) {
      this.hideLoadingState();
    }

    // Handle the case where the product is out of stock
    if (response.status === 422) {
      window.eventBus.emit('render:cart:drawer');
      this.hideLoadingState();
      if (this.qtyErrorMessage) this.qtyErrorMessage.classList.remove('hidden');
    }

    // Emit the cart:added event
    eventBus.emit('cart:added', { sectionId: this.sectionId });
    // Re-enable the blank inputs
    this.enableBlankInputs();
  }

  openCartDrawer() {
    /* ===== Open the cart drawer ===== */
    eventBus.emit('open:cart:drawer', { scrollToTop: true });
  }

  showLoadingState() {
    /* ===== Show the loading state of the add to cart button ===== */
    this.toggleElements(this.addToCartText, false);
    this.toggleElements(this.loadingIcon, true, 'hidden');
    this.toggleAddButton(true);
  }

  hideLoadingState() {
    /* ===== Hide the loading state of the add to cart button ===== */
    this.toggleElements(this.loadingIcon, false, 'hidden');
    this.toggleCartActionDisplay();
  }

  toggleCartActionDisplay() {
    /* ===== Toggle the cart action display based on the selected action ===== */
    if (this.cartAction === 'show_added_message' && this.addedText) {
      // Show the added text message and update the cart count indicator
      this.addedText.style.display = 'block';
      setTimeout(() => {
        this.toggleAddToCartText(true);
        this.toggleAddButton(false); 
        this.updateCartCountIndicator();
      }, 2000);
    } else if (this.cartType === 'drawer' && this.cartAction === 'go_to_or_open_cart') {
      // Open the cart drawer 
      this.toggleAddToCartText(true);
      this.openCartDrawer();
      this.toggleAddButton(false);
      this.updateCartCountIndicator();
    } else {
      // Redirect to the cart page
      this.toggleAddToCartText(true);
      this.toggleAddButton(false);
      window.location = window.routes.cart_url;
    }
  }

  toggleAddToCartText(show) {
    /* ===== Toggle the add to cart text and the added text message ===== */
    // Show the added text message and hide the add to cart text
    if (this.addedText) this.addedText.style.display = show ? 'none' : 'block';
    // Show the add to cart text and hide the added text message
    if (this.addToCartText) this.addToCartText.style.display = show ? 'block' : 'none';
  }

  toggleElements(element, show, className) {
    /* ===== Toggle the display of the element based on the show value ===== */
    if (!element) return;
    if (className) {
      show ? element.classList.remove(className) : element.classList.add(className);
    } else {
      element.style.display = show ? 'block' : 'none';
    }
  }

  toggleAddButton(disable = true) {
    /* ===== Toggle the state of the add to cart button ===== */
    const addButton = this.querySelector('[name="add"]');
    if (!addButton) return;

    addButton.disabled = disable;
  }

  // Estimated Delivery check starts
addPincodeChecker() {
  // Find the Add to Cart button
  const addToCartButton = this.querySelector('[name="add"]');
  if (!addToCartButton) return;

  // Check if viewport meta tag exists, if not, create it
  let viewportMeta = document.querySelector('meta[name="viewport"]');
  if (!viewportMeta) {
    viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1, maximum-scale=1';
    document.head.appendChild(viewportMeta);
  } else {
    // Update existing viewport meta tag
    const currentContent = viewportMeta.content;
    if (!currentContent.includes('maximum-scale=1')) {
      viewportMeta.content = currentContent + ', maximum-scale=1';
    }
  }

  // Create Pincode Input Field - match the width of the Add to Cart button
  const pincodeContainer = document.createElement("div");
  pincodeContainer.className = "pincode-checker";
  pincodeContainer.style.marginBottom = "20px";
  pincodeContainer.style.width = "100%"; // Full width to match buttons container

  // Create header text
  const headerText = document.createElement("h3");
  headerText.textContent = "Estimated Delivery Time";
  headerText.style.fontSize = "13px";
  headerText.style.fontWeight = "normal";
  headerText.style.marginBottom = "15px";
  headerText.style.marginTop = "0";
  headerText.style.fontFamily = '"Amiri", serif'; 
  pincodeContainer.appendChild(headerText);
  
  // Create input container - match button container's width
  const inputContainer = document.createElement("div");
  inputContainer.style.display = "flex";
  inputContainer.style.width = "100%"; // Full width to match buttons
  inputContainer.style.position = "relative";
  inputContainer.style.border = "1px solid #ccc";
  inputContainer.style.borderRadius = "5px";
  inputContainer.style.overflow = "hidden";
  inputContainer.style.height = "48px"; // Match button height
  inputContainer.style.boxSizing = "border-box"; // Include border in width calculation
  
  // Create input
  const input = document.createElement("input");
  input.id = "pincode-input";
  input.type = "text";
  input.placeholder = "Enter 6 digit pincode";
  input.maxLength = 6;
  input.style.padding = "0 15px";
  input.style.height = "100%";
  input.style.border = "none";
  input.style.outline = "none";
  input.style.width = "100%";
  input.style.fontFamily = '"Amiri", serif'; 
  input.style.fontSize = "13px";
  input.style.boxSizing = "border-box";
  inputContainer.appendChild(input);
  
 
  // Create check button
  const button = document.createElement("button");
  button.id = "check-pincode-btn";
  button.type = "button";
  button.textContent = "Check"; // Changed from all caps to just first letter capitalized
  button.style.backgroundColor = "#EDEDED"; // Changed from black to light grey
  button.style.color = "#000000"; // Changed text color to black for better visibility on grey
  button.style.border = "none";
  button.style.height = "100%";
  button.style.cursor = "pointer";
  button.style.fontWeight = "normal";
  button.style.fontFamily = '"Amiri", serif'; 
  button.style.position = "absolute";
  button.style.right = "0";
  button.style.top = "0";
  button.style.padding = "0 20px";
  button.style.minWidth = "90px";
  button.style.display = "flex";
  button.style.alignItems = "center";
  button.style.justifyContent = "center";
  button.style.textTransform = "none"; // Removed text transform to preserve "Check" as written
  inputContainer.appendChild(button);
  
  pincodeContainer.appendChild(inputContainer);
  
  // Create result elements
  const deliveryText = document.createElement("p");
  deliveryText.id = "estimated-delivery-text";
  deliveryText.style.fontSize = "14px";
  deliveryText.style.margin = "10px 0";
  deliveryText.style.color = "#000000";
  pincodeContainer.appendChild(deliveryText);
  
  const loader = document.createElement("div");
  loader.id = "delivery-loader";
  loader.style.display = "none";
  loader.style.margin = "10px 0";
  loader.innerHTML = '<span>Checking delivery availability...</span>';
  pincodeContainer.appendChild(loader);

  // Find where to insert the pincode checker
  // Find the same container that holds the Add to Cart button to ensure matching width
  const buttonContainer = addToCartButton.closest('.product-form__buttons') || 
                         addToCartButton.closest('.product-form__submit-container') || 
                         addToCartButton.parentNode;
  
  // Insert before the Add to Cart button but within the same container if possible
  buttonContainer.insertBefore(pincodeContainer, addToCartButton);

  // Add responsive styles
  const styleElement = document.createElement('style');
  styleElement.textContent = `
    /* Make sure the pincode checker matches the width of buttons */
    .pincode-checker {
      width: 100%;
      box-sizing: border-box;
    }
    
    /* General responsive styles */
    @media screen and (max-width: 767px) {
      .pincode-checker h3 {
        font-size: 18px !important;
      }
      
      #pincode-input {
        font-size: 14px !important;
      }
      
      #check-pincode-btn {
        min-width: 70px !important;
        padding: 0 15px !important;
        font-size: 14px !important;
      }
    }
    
    /* Even smaller screens */
    @media screen and (max-width: 479px) {
      .pincode-checker h3 {
        font-size: 14px !important;
      }
      
      #check-pincode-btn {
        min-width: 60px !important;
        padding: 0 10px !important;
      }
    }
  `;
  document.head.appendChild(styleElement);

  // Add event listeners after inserting into DOM
  this.attachPincodeEvents();
}

attachPincodeEvents() {
  // Get the elements
  const pincodeInput = document.getElementById('pincode-input');
  const checkButton = document.getElementById('check-pincode-btn');
  
  if (!pincodeInput || !checkButton) return;

  // Input validation - only allow numbers and max 6 digits
  pincodeInput.addEventListener('input', function() {
    this.value = this.value.replace(/[^0-9]/g, '').substring(0, 6);
  });
  
  // Add event listener to fetch estimated delivery date
  checkButton.addEventListener('click', ProductForm.fetchEstimatedDelivery);
  
  // Also check on Enter key press
  pincodeInput.addEventListener('keypress', function(e) {
    if (e.key === 'Enter') {
      e.preventDefault(); // Prevent form submission
      document.getElementById('check-pincode-btn').click();
    }
  });
}

// Static method to fetch delivery estimate
static fetchEstimatedDelivery() {
  const inputField = document.getElementById('pincode-input');
  const deliveryTextElement = document.getElementById('estimated-delivery-text');
  const loaderElement = document.getElementById('delivery-loader');
  
  if (!inputField || !deliveryTextElement || !loaderElement) return;
  
  const destinationPin = inputField.value.trim();
  
  if (destinationPin.length !== 6 || isNaN(destinationPin)) {
    deliveryTextElement.innerHTML = '<span style="color: red;">Please enter a valid 6-digit pincode.</span>';
    return;
  }
  
  // Check cache first
  const now = new Date().getTime();
  const cacheKey = `delivery_${destinationPin}`;
  const cachedData = localStorage.getItem(cacheKey);
  
  if (cachedData) {
    try {
      const cache = JSON.parse(cachedData);
      // Use cache if it's less than 24 hours old
      if (now - cache.timestamp < 24 * 60 * 60 * 1000) {
        deliveryTextElement.innerHTML = '<span style="color: green; font-weight: bold;">✓</span> Estimated Delivery: <strong>' + cache.formattedDate + '</strong>';
        return;
      }
    } catch (e) {
      // Invalid cache, continue with API call
      console.error('Cache parse error:', e);
    }
  }
  
  // Show loader
  loaderElement.style.display = 'block';
  deliveryTextElement.innerHTML = '';
  
  // Make API call to your Vercel function
  fetch('https://delivery-estimator-sigma.vercel.app/api/delivery-estimate', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    },
    body: JSON.stringify({
      destinationPin: destinationPin,
      originPin: '560064', // Your warehouse pincode
      mot: 'E', // Express
      pdt: 'B2C' // B2C Product Type
    })
  })
  .then(response => {
    if (!response.ok) {
      throw new Error('Network response was not ok');
    }
    return response.json();
  })
  .then(data => {
    loaderElement.style.display = 'none';
    
    if (data.success) {
      const deliveryDate = new Date(data.data.expected_delivery_date);
      const formattedDate = new Intl.DateTimeFormat('en-IN', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      }).format(deliveryDate);
      
      // Save to cache
      localStorage.setItem(cacheKey, JSON.stringify({
        timestamp: now,
        formattedDate: formattedDate,
        rawData: data
      }));
      
      deliveryTextElement.innerHTML = '<span style="color: green; font-weight: bold;">✓</span> Estimated Delivery: <strong>' + formattedDate + '</strong>';
    } else {
      deliveryTextElement.innerHTML = '<span style="color: red;">Delivery not available to this pincode.</span>';
    }
  })
  .catch(error => {
    console.error('API Error:', error);
    loaderElement.style.display = 'none';
    deliveryTextElement.innerHTML = '<span style="color: red;">Failed to check delivery. Please try again.</span>';
  });
}

  // Estimated Delivery check ends

  disconnectedCallback() {
    /* ===== Remove the event listeners when the element is removed from the DOM ===== */
    this.form.removeEventListener('submit', this.onSubmitBound);
  }
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}
















//old-code

// /******/ (() => { // webpackBootstrap
// /* eslint-disable */
// class ProductForm extends HTMLElement {
//   constructor() {
//     super();

//     this.cacheDOMElements();
//     /* ===== Bind the event handlers in order to maintain the correct context of 'this' ===== */
//     this.onSubmitBound = this.onSubmit.bind(this);
//     /* ===== Remove the noscript elements from the DOM (javascript is enabled) ===== */
//     Array.from(this.noscriptElements).forEach(element => element.remove());
//   }

//   cacheDOMElements() {
//     /* ===== Cache the DOM elements ===== */
//     this.form = this.querySelector('form');
//     this.noscriptElements = this.querySelectorAll('noscript');
//     this.sectionId = this.getAttribute('data-section-id');
//     this.productInformation = this.closest('product-information');
//     this.cartAction = document.getElementById('PageContainer').dataset.cartAction;
//     this.cartType = document.getElementById('PageContainer').dataset.cartType;
//     this.cartCountIndicator = document.querySelector('[data-cart-count-indicator]');
//     this.giftWrapProductId = this.getAttribute('data-gift-wrap-product-id');
//     this.wethemeGlobal = document.querySelector('script#wetheme-global');
//     this.translationsObject = JSON.parse(this.wethemeGlobal.textContent);
//     this.qtyErrorMessage = this.querySelector('[data-qty-error-message]');
//   }

//   connectedCallback() {
//     /* ===== Set the initial state of the add to cart button ===== */
//     if (!this.form) return;
//     this.attachEventListeners();
//   }

//   attachEventListeners() {
//     /* ===== Attach the event listeners to the DOM elements ===== */
//     this.form.addEventListener('submit', this.onSubmitBound);
//   }

//   onSubmit(event) {
//     /* ===== Handle the form submission ===== */
//     event.preventDefault();
//     this.prepareForSubmission();
//   }

//   prepareForSubmission() {
//     /* ===== Prepare the form for submission ===== */
//     this.cacheSubmissionElements();
//     if (!this.checkRequiredInputs()) return;
//     this.disableBlankInputs();
//     // If the form is valid, proceed with the submission
//     this.showLoadingState();
//     this.addItemsToCart();
//   }

//   cacheSubmissionElements() {
//     /* ===== Cache the elements needed for form submission ===== */
//     this.loadingIcon = this.form.querySelector('[data-loading-icon]');
//     this.addToCartText = this.form.querySelector('[data-add-to-cart-text]');
//     this.addedText = this.form.querySelector('[data-cart-added-text]');
//     this.quantityInput = this.form.querySelector('[name="quantity"]');
//     this.quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;
//   }

//   disableBlankInputs() {
//     // Select all input and textarea elements
//     const elements = this.productInformation.querySelectorAll('input, textarea');
//     this.blankElements = [];

//     elements.forEach((element) => {
//       // Check if the element has no value
//       if (!element.value) {
//         // Temporarily disable the element so that it is not included in the form submission
//         element.disabled = true;
//         this.blankElements.push(element);
//       }
//     });
//   }

//   enableBlankInputs() {
//     // Enable the blank elements that were disabled
//     this.blankElements.forEach((element) => {
//       element.disabled = false;
//     });
//     this.blankElements = [];
//   }

//   fetchConfig(type = 'json') {
//     /* ===== Create the fetch configuration object ===== */
//     return {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
//     };
//   }

//   checkRequiredInputs() {
//     /* ===== Check if the required inputs have been filled out ===== */
//     const requiredInputs = Array.from(this.productInformation.querySelectorAll('[required]:not(:disabled)'));
//     this.clearErrorMessages();
  
//     const invalidInputs = requiredInputs.filter(input => {
//       if (input.type === 'checkbox') {
//         return !input.checked;
//       } else {
//         return !input.value.trim();
//       }
//     });
  
//     invalidInputs.forEach(input => this.applyErrorMessage(input));
//     return invalidInputs.length === 0;
//   }  

//   clearErrorMessages() {
//     /* ===== Clear the error messages from the form ===== */
//     this.productInformation.querySelectorAll('.required-field-error').forEach(error => error.remove());
//   }

//   applyErrorMessage(input) {
//     /* ===== Apply the error message to the input element if the required input is empty ===== */
//     if (!input) return;
//     const inputId = input.id;
//     const buttonWrapper = this.querySelector('.paymentButtonsWrapper');
//     const errorContainer = input.closest('.input-wrapper')?.querySelector('.input-error-container');
//     const errorMessage = input.hasAttribute('custom-dropdown-input') ? this.translationsObject.translations.required_dropdown_error : input.type === 'file' ? this.translationsObject.translations.required_upload_error : input.type === 'checkbox' ? this.translationsObject.translations.required_checkbox_error : this.translationsObject.translations.required_input_error;
//     const errorElement = `<span id="${inputId}" class="required-field-error errors" style="display: block;">${errorMessage}</span>`;
//     const submitErrorMessage = input.type == 'file' ? input.closest('file-upload')?.dataset.errorMessage : input.dataset.errorMessage;
//     const submitErrorElement = `<span id="${inputId}Submit" class="required-field-error errors" style="display: block;">${submitErrorMessage}</span>`;
//     errorContainer ? errorContainer.innerHTML = errorElement : input.insertAdjacentHTML('beforebegin', errorElement);
//     if (buttonWrapper) buttonWrapper.insertAdjacentHTML('beforebegin', submitErrorElement);
//     input.focus();

//     // If the input has an error message, add an 'input' event listener to remove the error message
//     if (input.type == 'file') return;
//     input.addEventListener('input', () => {
//       this.productInformation.querySelector(`#${inputId}.required-field-error`)?.remove();
//       this.querySelector(`#${inputId}Submit.required-field-error`)?.remove();
//     }, { once: true });
//   }

//   giftWrapEnabled() {
//     /* ===== Determine if the gift wrap product is enabled ===== */
//     return !!this.productInformation.querySelector('[name="properties[Gift wrap]"]')?.checked && this.giftWrapProductId !== '';
//   }

//   async addItemsToCart() {
//     /* ===== Add the item(s) to the cart ===== */
//     try {
//       // Add the main product to the cart
//       const mainProductAdded = await this.addProductToCart(routes.cart_add_url, this.fetchConfigWithBody());
//       if (mainProductAdded && this.giftWrapEnabled()) {
//         // Add the gift wrap product to the cart
//         await this.addProductToCart(routes.cart_add_url, this.additionalProductConfig(this.giftWrapProductId, this.quantity));
//       }
//       // Process the response
//       this.processResponse(mainProductAdded);
//     } catch (error) {
//       console.error(`Error adding item(s) to cart: ${error.description || error.message}`);
//     }
//   }

//   async addProductToCart(url, config) {
//     /* ===== Add the product to the cart ===== */
//     const response = await fetch(url, config);
//     if (response.ok) {
//       // Update cart drawer
//       if (this.cartType === 'drawer') {
//         const responseJson = await response.json();
//         this.updateCartDrawer(responseJson);
//       }
//     } else {
//       console.error(`Error adding item to cart: ${response.description || response.statusText}`);
//     }
//     return response;
//   }

//   additionalProductConfig(productId, quantity) {
//     /* ===== Create the configuration object for the additional product(s) to be added to the cart ===== */
//     const additionalItemPayload = { items: [{ id: productId, quantity: quantity }] };
//     if (this.cartType === 'drawer') {
//       additionalItemPayload['sections'] = 'cart-drawer';
//     }
//     return {
//       method: 'POST',
//       headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
//       body: JSON.stringify(additionalItemPayload)
//     };
//   }

//   updateCartDrawer(responseJson) {
//     eventBus.emit('update:cart:drawer', responseJson);
//   }

//   async getCart() {
//     /* ===== Fetch the cart information ===== */
//     try {
//       const response = await fetch(`${routes.cart_url}?view=compare`);
//       return await response.json();
//     } catch (error) {
//       console.error(`Error fetching cart: ${error.description || error.message}`);
//     }
//   }

//   updateCartCountIndicator() {
//     /* ===== Update the cart count indicator ===== */
//     if (!this.cartCountIndicator) return;
//     this.getCart().then(cart => window.wetheme.updateCartCount(cart));
//   }

//   fetchConfigWithBody() {
//     /* ===== Create the fetch configuration object with the form data as the body ===== */
//     const config = this.fetchConfig('javascript');
//     config.headers['X-Requested-With'] = 'XMLHttpRequest';
//     delete config.headers['Content-Type'];
//     config.body = this.addSectionsParam(new FormData(this.form));
//     return config;
//   }

//   addSectionsParam(formData) {
//     /* ===== Add the sections parameter to the form data for cart drawer rendering ===== */
//     if (this.cartType === 'drawer') {
//       formData.append('sections', 'cart-drawer');
//     }
//     return formData;
//   }

//   processResponse(response) {
//     /* ===== Process the response from the server ===== */
//     if (response.ok) {
//       this.hideLoadingState();
//     }

//     // Handle the case where the product is out of stock
//     if (response.status === 422) {
//       window.eventBus.emit('render:cart:drawer');
//       this.hideLoadingState();
//       if (this.qtyErrorMessage) this.qtyErrorMessage.classList.remove('hidden');
//     }

//     // Emit the cart:added event
//     eventBus.emit('cart:added', { sectionId: this.sectionId });
//     // Re-enable the blank inputs
//     this.enableBlankInputs();
//   }

//   openCartDrawer() {
//     /* ===== Open the cart drawer ===== */
//     eventBus.emit('open:cart:drawer', { scrollToTop: true });
//   }

//   showLoadingState() {
//     /* ===== Show the loading state of the add to cart button ===== */
//     this.toggleElements(this.addToCartText, false);
//     this.toggleElements(this.loadingIcon, true, 'hidden');
//     this.toggleAddButton(true);
//   }

//   hideLoadingState() {
//     /* ===== Hide the loading state of the add to cart button ===== */
//     this.toggleElements(this.loadingIcon, false, 'hidden');
//     this.toggleCartActionDisplay();
//   }

//   toggleCartActionDisplay() {
//     /* ===== Toggle the cart action display based on the selected action ===== */
//     if (this.cartAction === 'show_added_message' && this.addedText) {
//       // Show the added text message and update the cart count indicator
//       this.addedText.style.display = 'block';
//       setTimeout(() => {
//         this.toggleAddToCartText(true);
//         this.toggleAddButton(false); 
//         this.updateCartCountIndicator();
//       }, 2000);
//     } else if (this.cartType === 'drawer' && this.cartAction === 'go_to_or_open_cart') {
//       // Open the cart drawer 
//       this.toggleAddToCartText(true);
//       this.openCartDrawer();
//       this.toggleAddButton(false);
//       this.updateCartCountIndicator();
//     } else {
//       // Redirect to the cart page
//       this.toggleAddToCartText(true);
//       this.toggleAddButton(false);
//       window.location = window.routes.cart_url;
//     }
//   }

//   toggleAddToCartText(show) {
//     /* ===== Toggle the add to cart text and the added text message ===== */
//     // Show the added text message and hide the add to cart text
//     if (this.addedText) this.addedText.style.display = show ? 'none' : 'block';
//     // Show the add to cart text and hide the added text message
//     if (this.addToCartText) this.addToCartText.style.display = show ? 'block' : 'none';
//   }

//   toggleElements(element, show, className) {
//     /* ===== Toggle the display of the element based on the show value ===== */
//     if (!element) return;
//     if (className) {
//       show ? element.classList.remove(className) : element.classList.add(className);
//     } else {
//       element.style.display = show ? 'block' : 'none';
//     }
//   }

//   toggleAddButton(disable = true) {
//     /* ===== Toggle the state of the add to cart button ===== */
//     const addButton = this.querySelector('[name="add"]');
//     if (!addButton) return;

//     addButton.disabled = disable;
//   }

//   disconnectedCallback() {
//     /* ===== Remove the event listeners when the element is removed from the DOM ===== */
//     this.form.removeEventListener('submit', this.onSubmitBound);
//   }
// }

// if (!customElements.get('product-form')) {
//   customElements.define('product-form', ProductForm);
// }

// /******/ })()
// ;
